import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import type { Feature, FeatureSet, PostingMode } from '@outss/ledger-core';
import { api, ApiError } from './api';
import { auth } from './auth';
import { useClients, useMe } from './clients';
import { useOrgId } from './org';

// ---------------------------------------------------------------- roles and Business view

export type OrgRole = 'outss_admin' | 'outss_accountant' | 'client_owner' | 'client_viewer';

/** The signed-in user's role in the current client (from the client switcher's list). */
export function useOrgRole(): OrgRole | null {
  const orgId = useOrgId();
  const clients = useClients(auth.enabled);
  const me = useMe(auth.enabled);
  const role = clients.data?.find((c) => c.id === orgId)?.role as OrgRole | undefined;
  if (role) return role;
  // sign-in disabled (local development): treat as staff
  if (!auth.enabled) return 'outss_accountant';
  return me.data?.isStaff ? 'outss_accountant' : null;
}

export const isClientRole = (r: OrgRole | null): boolean =>
  r === 'client_owner' || r === 'client_viewer';

const PREVIEW_KEY = 'outss.businessViewPreview';
const listeners = new Set<() => void>();
const readPreview = () => window.localStorage.getItem(PREVIEW_KEY) === '1';

/** Outss staff can look at a client's books as the client sees them (document 05). */
export function setBusinessViewPreview(on: boolean): void {
  if (on) window.localStorage.setItem(PREVIEW_KEY, '1');
  else window.localStorage.removeItem(PREVIEW_KEY);
  listeners.forEach((l) => l());
}

export function useBusinessViewPreview(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    readPreview,
    () => false,
  );
}

/** Business view: client users always; Outss staff while previewing it. */
export function useBusinessView(): { on: boolean; preview: boolean; role: OrgRole | null } {
  const role = useOrgRole();
  const preview = useBusinessViewPreview();
  const client = isClientRole(role);
  return { on: client || preview, preview: !client && preview, role };
}

// ---------------------------------------------------------------- portal settings and summary

export type { Feature, FeatureSet, PostingMode } from '@outss/ledger-core';

/** GET /orgs/:orgId/features (ADR-0017): a key means on, the value says who posts. */
export interface FeaturesView {
  features: FeatureSet;
  catalogue: Array<{
    feature: Feature;
    label: string;
    covers: string;
    requires: readonly (readonly Feature[])[];
    postable: boolean;
  }>;
}

export function useOrgFeatures() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['features', orgId],
    queryFn: () => api<FeaturesView>(`/orgs/${orgId}/features`),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

/** The open client's feature list; undefined until loaded (menus show everything meanwhile). */
export function useFeatureSet(): FeatureSet | undefined {
  return useOrgFeatures().data?.features;
}

export function useSaveFeatures() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (features: FeatureSet) =>
      api<FeaturesView>(`/orgs/${orgId}/features`, {
        method: 'PUT',
        body: JSON.stringify({ features }),
      }),
    onSuccess: (v) => qc.setQueryData(['features', orgId], v),
  });
}

/** Who posts a feature's documents; null when the feature is off (or the list is not loaded yet). */
export const postingOf = (set: FeatureSet | undefined, feature: Feature): PostingMode | null =>
  set?.[feature] ?? null;

/** Whether an entry tagged with features (any of them) is shown; untagged entries always are. */
export const featureShown = (set: FeatureSet | undefined, features?: readonly Feature[]): boolean =>
  !features || set === undefined || features.some((f) => f in set);

export interface FileInfo {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: 'not_scanned' | 'clean' | 'infected';
  uploadedBy: string | null;
  createdAt: string;
}

export type InboxStatus = 'received' | 'in_review' | 'recorded' | 'rejected';

export interface InboxItem {
  id: string;
  file: FileInfo;
  kind: 'bill' | 'receipt' | 'other';
  note: string | null;
  status: InboxStatus;
  rejectReason: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  processedBy: string | null;
  processedAt: string | null;
  document: {
    id: string;
    kind: string;
    number: string | null;
    status: string;
    contactName: string;
    total: string;
  } | null;
  version: number;
  /** Sent to the AI Accountant, with its status there. */
  aiDocument: { id: string; status: string } | null;
}

export interface PortalSummary {
  cash: {
    total: string;
    accounts: Array<{ id: string; name: string; subtype: string; balance: string }>;
  };
  receivables: { open: string; overdue: string; count: number; overdueCount: number };
  payables: { open: string; overdue: string; count: number; overdueCount: number };
  vat: { key: string; label: string; dueDate: string; status: string; netVat: string } | null;
  actions: {
    approvalsForMe: number;
    submittedPending: number;
    rejectedDrafts: number;
    inboxOpen: number;
    readyToPost: number;
  };
  recentUploads: InboxItem[];
  currency: string;
}

export function usePortalSummary() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'summary'],
    queryFn: () => api<PortalSummary>(`/orgs/${orgId}/portal/summary?view=business`),
    enabled: Boolean(orgId),
  });
}

// ---------------------------------------------------------------- inbox

export const INBOX_STATUS_LABEL: Record<InboxStatus, string> = {
  received: 'Received',
  in_review: 'In review',
  recorded: 'Recorded',
  rejected: 'Not recorded',
};

export const INBOX_KIND_LABEL: Record<InboxItem['kind'], string> = {
  bill: 'Bill',
  receipt: 'Receipt',
  other: 'Other',
};

export type InboxFilter = 'open' | 'recorded' | 'rejected' | 'all';

export function useInbox(filter: InboxFilter = 'all') {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'inbox', filter],
    queryFn: () => api<InboxItem[]>(`/orgs/${orgId}/inbox?status=${filter}`),
    enabled: Boolean(orgId),
  });
}

export function useInboxItem(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'inbox-item', id],
    queryFn: () => api<InboxItem>(`/orgs/${orgId}/inbox/${id}`),
    enabled: Boolean(orgId && id),
  });
}

// ---------------------------------------------------------------- files

/** What may be uploaded (checked again by content on the server). */
export const UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*';
export const MAX_UPLOAD_MB = 20;

/** Problems the browser can see before sending: empty, too large, not a PDF or photo. */
export function uploadProblem(f: { name: string; size: number; type: string }): string | null {
  if (!f.size) return `${f.name} is empty.`;
  if (f.size > MAX_UPLOAD_MB * 1024 * 1024) return `${f.name} is larger than ${MAX_UPLOAD_MB} MB.`;
  const ext = f.name.toLowerCase().split('.').pop() ?? '';
  const okType = /^(application\/pdf|image\/(png|jpeg|webp|heic|heif))$/.test(f.type);
  const okExt = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'heic'].includes(ext);
  return okType || okExt ? null : `${f.name} is not a PDF or a photo.`;
}

const contentTypeOf = (f: File) =>
  f.type ||
  (
    { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' } as Record<
      string,
      string
    >
  )[f.name.toLowerCase().split('.').pop() ?? ''] ||
  'application/octet-stream';

/** Sends the raw file; its name travels URI-encoded in x-file-name. */
export function uploadFile<T>(path: string, file: File): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    body: file,
    headers: { 'content-type': contentTypeOf(file), 'x-file-name': encodeURIComponent(file.name) },
  });
}

export function fileUrl(orgId: string, fileId: string, download = false): string {
  return `/orgs/${orgId}/files/${fileId}${download ? '?download=1' : ''}`;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type EntityType = 'document' | 'journal_entry' | 'payment';

export interface Attachment extends FileInfo {
  attachmentId: string;
  entityType: EntityType;
  entityId: string;
}

export interface AttachmentRow extends Attachment {
  entityLabel: string;
  entityLink: string | null;
}

export function useAttachments(entityType: EntityType, entityId: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'attachments', entityType, entityId],
    queryFn: () =>
      api<Attachment[]>(`/orgs/${orgId}/attachments?entityType=${entityType}&entityId=${entityId}`),
    enabled: Boolean(orgId && entityId),
  });
}

// ---------------------------------------------------------------- approvals

export interface Approval {
  id: string;
  direction: 'to_staff' | 'to_client';
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  note: string | null;
  requestedBy: string | null;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  document: {
    id: string;
    kind: 'invoice' | 'bill';
    number: string | null;
    status: string;
    contactName: string;
    issueDate: string;
    dueDate: string | null;
    currency: string;
    total: string;
  };
  canDecide: boolean;
  canWithdraw: boolean;
  readyToPost: boolean;
}

export function useApprovals(state: 'pending' | 'decided' | 'all' = 'pending') {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'approvals', state],
    queryFn: () => api<Approval[]>(`/orgs/${orgId}/approvals?state=${state}`),
    enabled: Boolean(orgId),
  });
}

export function useDocumentApprovals(documentId: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'approvals', 'document', documentId],
    queryFn: () => api<Approval[]>(`/orgs/${orgId}/approvals/documents/${documentId}`),
    enabled: Boolean(orgId && documentId),
  });
}

export const documentPath = (kind: string, id: string): string =>
  `${kind === 'invoice' ? '/sales/invoices' : kind === 'expense' ? '/expenses/expenses' : '/expenses/bills'}/${id}`;

export const APPROVAL_STATUS_LABEL: Record<Approval['status'], string> = {
  pending: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Withdrawn',
};

/** Where an approval stands, in words for the queue and the document strip. */
export function approvalState(a: Approval): string {
  if (a.readyToPost) return 'Approved by the client, ready to post';
  if (a.status === 'pending')
    return a.direction === 'to_client' ? 'Awaiting the client' : 'Awaiting the accountant';
  return APPROVAL_STATUS_LABEL[a.status];
}

/**
 * Whether the signed-in client owner may edit this draft: client drafts switched on, an invoice or
 * a bill, and either new or one they submitted that Outss has not posted.
 */
export function clientCanEdit(input: {
  role: OrgRole | null;
  /** How the document's feature is posted for this client (ADR-0017). */
  posting: PostingMode | null;
  kind: string;
  doc: { status: string } | null;
  approvals: Approval[] | undefined;
}): boolean {
  if (input.role !== 'client_owner' || !input.posting || input.posting === 'staff') return false;
  // direct: the client keeps this part of the books (void and repost as staff do)
  if (input.posting === 'direct') return !input.doc || input.doc.status !== 'void';
  if (input.kind !== 'invoice' && input.kind !== 'bill') return false;
  if (!input.doc) return true;
  return (
    input.doc.status === 'draft' && (input.approvals ?? []).some((a) => a.direction === 'to_staff')
  );
}

/**
 * Whether a sales or purchase form is read-only. Outss staff, or a client owner allowed to edit
 * this draft (`clientCanEdit`), may change it, unless it is void, a converted quote, an opening
 * document brought over from QuickBooks (corrected through the migration, never edited), or
 * locked by its e-invoice.
 */
export function formReadOnly(input: {
  isStaff: boolean;
  clientEdit: boolean;
  doc: { status: string; opening?: boolean | null } | null;
  einvoiceLocked?: boolean;
}): boolean {
  if (!(input.isStaff || input.clientEdit)) return true;
  const d = input.doc;
  if (!d) return false;
  return (
    d.status === 'void' ||
    d.status === 'converted' ||
    Boolean(d.opening) ||
    Boolean(input.einvoiceLocked)
  );
}

// ---------------------------------------------------------------- notifications

export type NotificationEvent =
  | 'approval_requested'
  | 'approval_decided'
  | 'upload_received'
  | 'upload_processed'
  | 'vat_return_due';

export interface AppNotification {
  id: string;
  event: NotificationEvent;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  readAt: string | null;
  emailStatus: string;
}

export const NOTIFICATION_LABEL: Record<NotificationEvent, { title: string; hint: string }> = {
  approval_requested: {
    title: 'Documents awaiting approval',
    hint: 'A draft invoice or bill is waiting for your approval.',
  },
  approval_decided: {
    title: 'Approval decisions',
    hint: 'A document you sent for approval was approved or rejected.',
  },
  upload_received: {
    title: 'New uploads',
    hint: 'The client added a bill or receipt to the document inbox.',
  },
  upload_processed: {
    title: 'Uploads processed',
    hint: 'A bill or receipt you uploaded was recorded, or could not be.',
  },
  vat_return_due: {
    title: 'Tax return due',
    hint: 'A tax return is due within seven days and has not been filed.',
  },
};

export function useNotifications(enabled = true) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['portal', orgId, 'notifications'],
    queryFn: () =>
      api<{ unread: number; notifications: AppNotification[] }>(`/orgs/${orgId}/notifications`),
    enabled: Boolean(orgId) && enabled,
    refetchInterval: 60_000,
  });
}

export function useMarkRead() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      api<{ unread: number }>(`/orgs/${orgId}/notifications/read`, {
        method: 'POST',
        body: JSON.stringify(ids ? { ids } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', orgId, 'notifications'] }),
  });
}

/** Message of an API error for a toast or banner, first letter capitalised. */
export function errorText(e: unknown): string {
  const m = e instanceof ApiError || e instanceof Error ? e.message : String(e);
  return m.charAt(0).toUpperCase() + m.slice(1);
}
